import type { ApiItem } from "./api.ts";
import { wrapIndex } from "./carousel.ts";
import { availabilityLabel } from "./availability.ts";
import { renderPrice } from "./price.ts";

function requiredElement<T extends Element>(
    id: string,
    expectedType: { new (): T },
): T {
    const element = document.getElementById(id);

    if (!(element instanceof expectedType)) {
        throw new Error(`Required element "#${id}" is missing`);
    }

    return element;
}

export class Lightbox {
    readonly #dialog: HTMLDialogElement;
    readonly #closeButton: HTMLButtonElement;
    readonly #previousButton: HTMLButtonElement;
    readonly #nextButton: HTMLButtonElement;
    readonly #image: HTMLImageElement;
    readonly #imageContainer: HTMLDivElement;
    readonly #title: HTMLHeadingElement;
    readonly #description: HTMLParagraphElement;
    readonly #availability: HTMLParagraphElement;
    readonly #price: HTMLParagraphElement;
    readonly #counter: HTMLParagraphElement;
    readonly #priceFormatter: Intl.NumberFormat;

    #item: ApiItem | undefined;
    #index = 0;
    #opener: HTMLButtonElement | undefined;
    #touchStartX: number | undefined;

    constructor(priceFormatter: Intl.NumberFormat) {
        this.#priceFormatter = priceFormatter;
        this.#dialog = requiredElement(
            "item-dialog",
            HTMLDialogElement,
        );
        this.#closeButton = requiredElement(
            "dialog-close",
            HTMLButtonElement,
        );
        this.#previousButton = requiredElement(
            "dialog-previous",
            HTMLButtonElement,
        );
        this.#nextButton = requiredElement(
            "dialog-next",
            HTMLButtonElement,
        );
        this.#image = requiredElement(
            "dialog-image",
            HTMLImageElement,
        );
        this.#imageContainer = requiredElement(
            "dialog-image-container",
            HTMLDivElement,
        );
        this.#availability = requiredElement(
            "dialog-availability",
            HTMLParagraphElement,
        );
        this.#title = requiredElement(
            "dialog-title",
            HTMLHeadingElement,
        );
        this.#description = requiredElement(
            "dialog-description",
            HTMLParagraphElement,
        );
        this.#price = requiredElement(
            "dialog-price",
            HTMLParagraphElement,
        );
        this.#counter = requiredElement(
            "dialog-counter",
            HTMLParagraphElement,
        );

        this.#closeButton.addEventListener("click", () => {
            this.#dialog.close();
        });

        this.#previousButton.addEventListener("click", () => {
            this.#move(-1);
        });

        this.#nextButton.addEventListener("click", () => {
            this.#move(1);
        });

        this.#dialog.addEventListener("keydown", (event) => {
            this.#handleKeydown(event);
        });

        this.#dialog.addEventListener("click", (event) => {
            if (event.target === this.#dialog) {
                this.#dialog.close();
            }
        });

        this.#dialog.addEventListener("close", () => {
            this.#handleClose();
        });

        this.#imageContainer.addEventListener(
            "touchstart",
            (event) => {
                const touch = event.touches.item(0);
                this.#touchStartX = touch?.clientX;
            },
            { passive: true },
        );

        this.#imageContainer.addEventListener(
            "touchend",
            (event) => {
                this.#handleTouchEnd(event);
            },
            { passive: true },
        );

        this.#imageContainer.addEventListener(
            "touchcancel",
            () => {
                this.#touchStartX = undefined;
            },
            { passive: true },
        );
    }

    open(
        item: ApiItem,
        opener: HTMLButtonElement,
    ): void {
        this.#item = item;
        this.#index = item.coverIndex;
        this.#opener = opener;
        this.#render();

        document.documentElement.classList.add("dialog-open");

        if (!this.#dialog.open) {
            this.#dialog.showModal();
        }

        this.#closeButton.focus();
    }

    #move(offset: number): void {
        const item = this.#item;

        if (!item || item.images.length <= 1) {
            return;
        }

        this.#index = wrapIndex(
            this.#index + offset,
            item.images.length,
        );
        this.#render();
    }

    #handleKeydown(event: KeyboardEvent): void {
        switch (event.key) {
            case "ArrowLeft":
                event.preventDefault();
                this.#move(-1);
                break;

            case "ArrowRight":
                event.preventDefault();
                this.#move(1);
                break;

            case "Home":
                event.preventDefault();
                this.#index = 0;
                this.#render();
                break;

            case "End": {
                const item = this.#item;

                if (item) {
                    event.preventDefault();
                    this.#index = item.images.length - 1;
                    this.#render();
                }
                break;
            }
        }
    }

    #handleTouchEnd(event: TouchEvent): void {
        const startX = this.#touchStartX;
        const touch = event.changedTouches.item(0);

        this.#touchStartX = undefined;

        if (startX === undefined || !touch) {
            return;
        }

        const distance = touch.clientX - startX;

        if (Math.abs(distance) < 50) {
            return;
        }

        this.#move(distance < 0 ? 1 : -1);
    }

    #render(): void {
        const item = this.#item;

        if (!item) {
            return;
        }

        const image = item.images[this.#index];

        if (!image) {
            throw new Error(
                `Item "${item.id}" has invalid image index ${this.#index}`,
            );
        }

        this.#image.src = image.gallery.url;
        this.#image.width = image.gallery.width;
        this.#image.height = image.gallery.height;
        this.#image.alt = `${item.name}, photo ${this.#index + 1} ` +
            `of ${item.images.length}`;

        this.#availability.textContent = availabilityLabel(item.availability);
        this.#title.textContent = item.name;
        this.#description.textContent = item.description;
        renderPrice(
            this.#price,
            item,
            this.#priceFormatter,
        );
        this.#counter.textContent = `Photo ${
            this.#index + 1
        } of ${item.images.length}`;

        const hasMultipleImages = item.images.length > 1;
        this.#previousButton.disabled = !hasMultipleImages;
        this.#nextButton.disabled = !hasMultipleImages;

        this.#preloadAdjacent();
    }

    #preloadAdjacent(): void {
        const item = this.#item;

        if (!item || item.images.length <= 1) {
            return;
        }

        const indexes = new Set([
            wrapIndex(this.#index - 1, item.images.length),
            wrapIndex(this.#index + 1, item.images.length),
        ]);

        for (const index of indexes) {
            const image = item.images[index];

            if (image) {
                const preload = new Image();
                preload.src = image.gallery.url;
            }
        }
    }

    #handleClose(): void {
        document.documentElement.classList.remove("dialog-open");
        this.#image.removeAttribute("src");
        this.#item = undefined;

        const opener = this.#opener;
        this.#opener = undefined;

        if (opener?.isConnected) {
            requestAnimationFrame(() => opener.focus());
        }
    }
}
